import { IAddress, IPharmacyAddress } from './common.dto';

export type Gender = 'male' | 'female' | 'other';

export interface CustomerConsent {
  status: 'accepted' | 'rejected';
  updatedAt: string;
}

export type CustomerUserExtras = {
  pharmetikaV2PatientId?: string;
  [key: string]: any;
};

export class ICustomerUser {
  _id: string;
  uname: string;
  // from user
  email: string;
  firstName: string;
  lastName: string;
  salt: string;
  hash: string;
  provinceCode?: string;
  // roles: string[];
  phone?: string;
  stripeId?: string;
  /** Whop member id (`user_…`) after first paid Whop checkout. */
  whopMemberId?: string;
  bigcommerceId?: string;
  referrer?: string;
  iat?: string;
  exp?: string;
  token?: string;
  age?: number;
  isInsuranceAvailable: boolean;
  identifiers?: IHealthCard[];
  // from customer
  organization: string;
  dob?: string;
  addresses?: IAddress[];
  defaultAddress?: Partial<IAddress>;
  tags: string;
  currency: string;
  notificationsCount: number;
  contactPreference: 'EMAIL' | 'SMS' | 'WHATSAPP';
  watchlist: string[];
  usedCoupons: string[];
  meta: IUserMeta;
  createdAt?: Date;
  conversationId?: string;
  assignedQuestionnaires?: string[];
  providers?: string[];
  groups?: string[];
  shippingPharmacy?: string;
  verifications: ICustomerVerification;
  passwordReset?: ICustomerPasswordReset;
  gender?: Gender;
  extras?: CustomerUserExtras;
  credit?: ICustomerCredit;
  hasDrugCardCoverage?: boolean;
  drugCards?: IDrugCard[];
  smsConsentAccepted?: boolean;
  consents?: Record<string, CustomerConsent>;
  lastContactedAt?: Date;
  lastEmailedAt?: Date;
  lastMessagedAt?: Date;
  pickupPharmacy?: IPharmacyAddress | null;
  pickupPharmacyId?: string | null;
}

export class NewCustomerDTO {
  email: string;
  password?: string;
  firstName: string;
  lastName: string;
  gender: Gender;
  phone: string;
  uname?: string;
  roles?: string[];
  dob: string;
  meta?: IUserMeta;
  notificationsCount?: number;
  emrId?: number;
  referrer?: string;
  source?: string;
  // why this is called hcCard not hc
  hcCard?: IHealthCard;
  smsConsentAccepted?: boolean;
  acceptMarketing?: boolean;
  consents?: Record<string, boolean>;
  isInsuranceAvailable?: boolean;
  providers?: string[];
  verifications?: ICustomerVerification;
  provinceCode?: string;
  extras?: any;
  groups?: string[];
}
export class UpdateCustomerDTO {
  dob?: string;
  uname?: string;
  // fullName?: string;
  roles?: string[];
  addresses?: IAddress[];
  defaultAddress?: IAddress;
  meta?: IUserMeta;
  notificationsCount?: number;
  chatId?: string;
  //  flattened from user
  firstName?: string;
  lastName?: string;
  provinceCode?: string;
  phone?: string;
  email?: string;
  source?: string;
  isInsuranceAvailable?: boolean;
  assignedQuestionnaires?: string[];
  identifiers?: IHealthCard[];
  providers?: string[];
  verifications?: ICustomerVerification;
  extras?: any;
  groups?: string[];
  shippingPharmacy?: string;
  hasDrugCardCoverage?: boolean;
  drugCards?: IDrugCard[];
  pickupPharmacy?: IPharmacyAddress | null;
  pickupPharmacyId?: string | null;
}

export class IGetCustomersDTO {
  firstName?: string;
  lastName?: string;
  email?: string;
  provinceCode?: string;
  provider?: string;
  providers?: string[];
  groups?: string[];
  bigcommerceId?: string;
  // Legacy cursor markers, kept for backwards compatibility.
  minId?: string;
  maxId?: string;
  // Server-side (offset) pagination.
  limit?: number;
  offset?: number;
  includeCount?: boolean;
  sortField?: string;
  sortOrder?: 1 | -1;
}

export class IUpdateUserPasswordDTO {
  currentPassword: string;
  newPassword: string;
}

export class IUpdateUserProfileDTO {
  firstName?: string;
  lastName?: string;
  dob: string;
  phone: string;
  extras: any;
  gender: Gender;
}
export type IPartialCustomer = Partial<ICustomerUser>;

export class IUserMeta {
  gender?: string;
  allergies?: string;
  weight?: number;
  temperature?: number;
  junctionId?: string;
  precisionPatientId?: string;
  /** Timestamp of the last customerServiceEmail sent for this customer. */
  lastSupportTicketAt?: Date | string;
  /** Blend patient UUID, set on first submission and reused for every later order. */
  blendPatientId?: string;
  [key: string]: any;
}

export class IHealthCard {
  hcExp?: string;
  hcEff?: string;
  hcNum?: string;
  hcVer?: string;
  hcType?: string;
  uid?: string;
  number: string;
  version?: string;
  effectiveDate?: string | Date;
  expiryDate?: string | Date;
  type?: string;
  groupNumber?: string;
}

export class IDrugCard {
  uid: string;
  label?: string;
  /** Primary, secondary, secondary 2, secondary 3, or provincial */
  type?: DrugCardType;
  /** Relationship to cardholder (e.g. self, spouse, dependent) */
  relationshipToCardholder?: string;
  /** Legacy single-image key. */
  fileKey?: string;
  /** Preferred front image key for the drug card. */
  frontFileKey?: string;
  /** Preferred back image key for the drug card. */
  backFileKey?: string;
  createdAt?: string;
}

export type DrugCardType =
  | 'primary'
  | 'secondary'
  | 'secondary2'
  | 'secondary3'
  | 'provincial';

export type ICustomerVerificationFields = {
  verified: VerifiedStatus;
  verifiedBy?: string;
  verificationId?: string;
  verifiedDate?: string;
  lastVerificationSentDate?: string;
  updatedAt?: string;
  expiry?: number;
  personaId?: string;
};

export type ICustomerVerification = {
  email?: ICustomerVerificationFields;
  phone?: VerifiedStatus;
  govId?: ICustomerVerificationFields;
};

export type VerifiedStatus =
  | 'required'
  | 'verified'
  | 'pending' // Persona pending verification
  | 'manual-check-required'
  | 'started' //Persona started
  | null;

export type ICustomerCreditType =
  | 'referrer'
  | 'referee'
  | 'withdrawal'
  | 'manual'
  | 'promotion'
  | 'refund';
export type ICustomerCredit = {
  amount: number;
  history: {
    amount: number;
    previousAmount: number;
    createdAt: Date;
    type: ICustomerCreditType;
    note?: string;
    orderId?: string | number;
    createdBy?: string;
    idempotencyKey?: string;
  }[];
};

export class ICustomerPasswordReset {
  code: string;
  expiry: number;
}

export class IResetPasswordResponseDTO {
  status: string;
  data: { username: string };
}

export class IGetPaymentMethodResponseDTO {
  id: string;
  metadata: string;
  type: string;
  created: number;
  billing_details: Object;
  card: {
    /**
     * Card brand. Can be `amex`, `diners`, `discover`, `jcb`, `mastercard`, `unionpay`, `visa`, or `unknown`.
     */
    brand: string;

    /**
     * Checks on Card address and CVC if provided.
     */
    checks: string;

    /**
     * Two-letter ISO code representing the country of the card. You could use this attribute to get a sense of the international breakdown of cards you've collected.
     */
    country: string | null;

    /**
     * A high-level description of the type of cards issued in this range. (For internal use only and not typically available in standard API requests.)
     */
    description?: string | null;

    /**
     * Two-digit number representing the card's expiration month.
     */
    exp_month: number;

    /**
     * Four-digit number representing the card's expiration year.
     */
    exp_year: number;

    /**
     * Uniquely identifies this particular card number. You can use this attribute to check whether two customers who've signed up with you are using the same card number, for example. For payment methods that tokenize card information (Apple Pay, Google Pay), the tokenized number might be provided instead of the underlying card number.
     *
     * *Starting May 1, 2021, card fingerprint in India for Connect will change to allow two fingerprints for the same card --- one for India and one for the rest of the world.*
     */
    fingerprint?: string | null;

    /**
     * Card funding type. Can be `credit`, `debit`, `prepaid`, or `unknown`.
     */
    funding: string;

    /**
     * Issuer identification number of the card. (For internal use only and not typically available in standard API requests.)
     */
    iin?: string | null;

    /**
     * The name of the card's issuing bank. (For internal use only and not typically available in standard API requests.)
     */
    issuer?: string | null;

    /**
     * The last four digits of the card.
     */
    last4: string;

    /**
     * Contains information about card networks that can be used to process the payment.
     */
    networks: string;

    /**
     * Contains details on how this Card maybe be used for 3D Secure authentication.
     */
    three_d_secure_usage: string;

    /**
     * If this Card is part of a card wallet, this contains the details of the card wallet.
     */
    wallet: string;
  };
}

export class IPaymentMethod {
  id?: string;
  last4: string;
  type: string;
  exp: string;
  zip?: string;
  card?: any; // for stripe legacy support
  isDefault?: boolean;
}

export class ICheckoutPaymentMethod {
  token: string;
  type?: 'vault' | 'card'; // card default
  zip?: string;
  cvv?: string;
}

export interface newCustomerPayload {
  email: string;
  password?: string;
  firstName: string;
  lastName: string;
  gender: 'male' | 'female';
  phone: string;
  uname?: string;
  dob: string;
  referrer?: string;
  acceptMarketing?: boolean;
  provinceCode?: string;
}
